import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';

type Language = 'ps' | 'da';

interface Props {
  language: Language;
}

const PAGES = ['home', 'mission', 'projects', 'volunteer', 'donate', 'contact'] as const;
const SECTIONS: Record<string, string[]> = {
  home: ['hero', 'mission', 'impact', 'projects_preview', 'volunteer_preview', 'donate_preview'],
  mission: ['hero', 'vision', 'values', 'cta'],
  projects: ['hero', 'list'],
  volunteer: ['hero', 'opportunities', 'testimonials'],
  donate: ['hero', 'tiers', 'how_to', 'cta'],
  contact: ['hero', 'form', 'info', 'multilingual'],
};

const ENGLISH_REF: Record<string, Record<string, { title?: string; subtitle?: string; body?: string; cta?: string }>> = {
  home: {
    hero: { title: 'Empowering Afghan Communities', subtitle: 'Building bridges between Canada and Afghanistan through education, healthcare, and sustainable development.', cta: 'Explore Our Work' },
    mission: { title: 'Our Mission', subtitle: 'To empower Afghan communities through education, healthcare, and economic opportunity.' },
    impact: { title: 'Our Impact', subtitle: 'Every contribution makes a difference in the lives of Afghan families.' },
    projects_preview: { title: 'Featured Projects', subtitle: 'Discover our initiatives creating lasting change.' },
    volunteer_preview: { title: 'Volunteer With Us', subtitle: 'Join our community of passionate volunteers making a difference.' },
    donate_preview: { title: 'Support Our Mission', subtitle: 'Your generosity helps us reach more communities in need.' },
  },
  mission: {
    hero: { title: 'Our Mission & Vision', subtitle: 'Understanding our purpose and the principles that guide our work.' },
    vision: { title: 'Our Vision', body: 'A world where every Afghan has access to education, healthcare, and the opportunity to build a dignified life — both in Afghanistan and in diaspora communities across Canada.' },
    values: { title: 'Core Values', body: 'Integrity, Compassion, Empowerment, Transparency, and Cultural Respect guide everything we do.' },
    cta: { title: 'Be Part of Our Mission', subtitle: 'Together, we can create lasting change for Afghan communities.' },
  },
  projects: {
    hero: { title: 'Our Projects', subtitle: 'Explore our active initiatives making a difference across Afghanistan and Canada.' },
    list: { title: 'All Projects', subtitle: 'Browse our complete portfolio of active and completed projects.' },
  },
  volunteer: {
    hero: { title: 'Volunteer With Us', subtitle: 'Share your skills and passion to make a meaningful impact.' },
    opportunities: { title: 'Current Opportunities', subtitle: 'Find the perfect role that matches your skills and availability.' },
    testimonials: { title: 'Volunteer Stories', subtitle: 'Hear from our volunteers about their experiences.' },
  },
  donate: {
    hero: { title: 'Support Our Mission', subtitle: 'Your contribution, no matter the size, helps us transform lives.' },
    tiers: { title: 'Donation Tiers', subtitle: 'Choose the giving level that feels right for you.' },
    how_to: { title: 'How to Donate', subtitle: 'Multiple ways to contribute to our cause.' },
    cta: { title: 'Every Dollar Counts', subtitle: '100% of your donation goes directly to our programs.' },
  },
  contact: {
    hero: { title: 'Get in Touch', subtitle: 'We would love to hear from you. Reach out with any questions or ideas.' },
    form: { title: 'Send Us a Message', subtitle: 'Fill out the form below and we will get back to you shortly.' },
    info: { title: 'Our Office', subtitle: 'Visit or contact us through any of the channels below.' },
    multilingual: { title: 'We Speak Your Language', subtitle: 'Support is available in English, French, Pashto, and Dari.' },
  },
};

const LANG_INFO: Record<Language, { label: string; nativeLabel: string; flag: string }> = {
  ps: { label: 'Pashto', nativeLabel: 'پښتو', flag: '🇦🇫' },
  da: { label: 'Dari', nativeLabel: 'دری', flag: '🇦🇫' },
};

type TabType = 'pages' | 'projects' | 'volunteers' | 'stories';

interface CmsForm {
  title: string;
  subtitle: string;
  body: string;
  cta: string;
}

interface ProjectRow {
  id: string;
  title_en: string;
  description_en: string;
  title_ps: string;
  title_da: string;
  description_ps: string;
  description_da: string;
}

interface VolunteerRow {
  id: string;
  title_en: string;
  description_en: string;
  title_ps: string;
  title_da: string;
  description_ps: string;
  description_da: string;
}

interface StoryRow {
  id: string;
  slug: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  excerpt_en: string;
  excerpt_ps: string;
  excerpt_da: string;
  content_en: string;
  content_ps: string;
  content_da: string;
  author_en: string;
  author_ps: string;
  author_da: string;
  author_role_en: string;
  author_role_ps: string;
  author_role_da: string;
  image: string;
  category: string;
  read_time_en: string;
  read_time_ps: string;
  read_time_da: string;
  published: boolean;
}

export default function LanguageCmsEditor({ language }: Props) {
  const [activeTab, setActiveTab] = useState<TabType>('pages');
  const [activePage, setActivePage] = useState<string>('home');
  const [activeSection, setActiveSection] = useState<string>('hero');
  const [form, setForm] = useState<CmsForm>({ title: '', subtitle: '', body: '', cta: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cmsEntries, setCmsEntries] = useState<Record<string, CmsForm>>({});

  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectRow | null>(null);
  const [projectForm, setProjectForm] = useState({ title: '', description: '' });

  const [opportunities, setOpportunities] = useState<VolunteerRow[]>([]);
  const [selectedOpp, setSelectedOpp] = useState<VolunteerRow | null>(null);
  const [oppForm, setOppForm] = useState({ title: '', description: '' });

  const [stories, setStories] = useState<StoryRow[]>([]);
  const [selectedStory, setSelectedStory] = useState<StoryRow | null>(null);
  const [storyForm, setStoryForm] = useState({ title: '', excerpt: '', content: '', author: '', author_role: '', read_time: '' });

  const [pageLoading, setPageLoading] = useState(false);
  const [projLoading, setProjLoading] = useState(false);
  const [oppLoading, setOppLoading] = useState(false);
  const [storyLoading, setStoryLoading] = useState(false);

  const info = LANG_INFO[language];
  const isPs = language === 'ps';

  // ============ Page Content ============
  const fetchCmsEntries = useCallback(async () => {
    setPageLoading(true);
    const { data } = await supabase.from('cms_content').select('page, section, content').eq('language', language);
    const map: Record<string, CmsForm> = {};
    (data ?? []).forEach((row: any) => {
      map[`${row.page}__${row.section}`] = {
        title: row.content?.title ?? '',
        subtitle: row.content?.subtitle ?? '',
        body: row.content?.body ?? '',
        cta: row.content?.cta ?? '',
      };
    });
    setCmsEntries(map);
    setPageLoading(false);
  }, [language]);

  useEffect(() => {
    if (activeTab === 'pages') fetchCmsEntries();
  }, [activeTab, fetchCmsEntries]);

  useEffect(() => {
    const key = `${activePage}__${activeSection}`;
    const saved = cmsEntries[key];
    setForm(saved ?? { title: '', subtitle: '', body: '', cta: '' });
  }, [activePage, activeSection, cmsEntries]);

  const handleSavePageContent = async () => {
    setSaving(true);
    const payload = {
      page: activePage,
      section: activeSection,
      language,
      content: form,
      updated_at: new Date().toISOString(),
    };
    await supabase.from('cms_content').upsert([payload], { onConflict: 'page,section,language' });
    setSaving(false);
    setSaved(true);
    fetchCmsEntries();
    setTimeout(() => setSaved(false), 2000);
  };

  // ============ Projects ============
  const fetchProjects = useCallback(async () => {
    setProjLoading(true);
    const titlePs = isPs ? 'title_ps' : 'title_da';
    const descPs = isPs ? 'description_ps' : 'description_da';
    const { data } = await supabase
      .from('projects')
      .select(`id, title_en, description_en, ${titlePs}, ${descPs}`)
      .order('created_at', { ascending: false });
    const rows = (data ?? []).map((r: any) => ({
      id: r.id,
      title_en: r.title_en,
      description_en: r.description_en,
      title_ps: r.title_ps ?? '',
      title_da: r.title_da ?? '',
      description_ps: r.description_ps ?? '',
      description_da: r.description_da ?? '',
    }));
    setProjects(rows);
    setProjLoading(false);
  }, [isPs]);

  useEffect(() => {
    if (activeTab === 'projects') fetchProjects();
  }, [activeTab, fetchProjects]);

  useEffect(() => {
    if (selectedProject) {
      setProjectForm({
        title: isPs ? selectedProject.title_ps : selectedProject.title_da,
        description: isPs ? selectedProject.description_ps : selectedProject.description_da,
      });
    }
  }, [selectedProject, isPs]);

  const handleSaveProject = async () => {
    if (!selectedProject) return;
    setSaving(true);
    const updates = isPs
      ? { title_ps: projectForm.title, description_ps: projectForm.description }
      : { title_da: projectForm.title, description_da: projectForm.description };
    await supabase.from('projects').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', selectedProject.id);
    setSaving(false);
    setSaved(true);
    fetchProjects();
    setTimeout(() => setSaved(false), 2000);
  };

  // ============ Volunteer Opportunities ============
  const fetchOpportunities = useCallback(async () => {
    setOppLoading(true);
    const { data } = await supabase
      .from('volunteer_opportunities')
      .select('id, title_en, description_en, title_ps, title_da, description_ps, description_da')
      .order('created_at', { ascending: false });
    setOpportunities((data as VolunteerRow[]) ?? []);
    setOppLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'volunteers') fetchOpportunities();
  }, [activeTab, fetchOpportunities]);

  useEffect(() => {
    if (selectedOpp) {
      setOppForm({
        title: isPs ? selectedOpp.title_ps : selectedOpp.title_da,
        description: isPs ? selectedOpp.description_ps : selectedOpp.description_da,
      });
    }
  }, [selectedOpp, isPs]);

  const handleSaveOpportunity = async () => {
    if (!selectedOpp) return;
    setSaving(true);
    const updates = isPs
      ? { title_ps: oppForm.title, description_ps: oppForm.description }
      : { title_da: oppForm.title, description_da: oppForm.description };
    await supabase.from('volunteer_opportunities').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', selectedOpp.id);
    setSaving(false);
    setSaved(true);
    fetchOpportunities();
    setTimeout(() => setSaved(false), 2000);
  };

  // ============ Stories ============
  const fetchStories = useCallback(async () => {
    setStoryLoading(true);
    const { data } = await supabase
      .from('stories')
      .select('*')
      .order('created_at', { ascending: false });
    setStories((data as StoryRow[]) ?? []);
    setStoryLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'stories') fetchStories();
  }, [activeTab, fetchStories]);

  useEffect(() => {
    if (selectedStory) {
      setStoryForm({
        title: isPs ? selectedStory.title_ps : selectedStory.title_da,
        excerpt: isPs ? selectedStory.excerpt_ps : selectedStory.excerpt_da,
        content: isPs ? selectedStory.content_ps : selectedStory.content_da,
        author: isPs ? selectedStory.author_ps : selectedStory.author_da,
        author_role: isPs ? selectedStory.author_role_ps : selectedStory.author_role_da,
        read_time: isPs ? selectedStory.read_time_ps : selectedStory.read_time_da,
      });
    }
  }, [selectedStory, isPs]);

  const handleSaveStory = async () => {
    if (!selectedStory) return;
    setSaving(true);
    const updates = isPs
      ? { title_ps: storyForm.title, excerpt_ps: storyForm.excerpt, content_ps: storyForm.content, author_ps: storyForm.author, author_role_ps: storyForm.author_role, read_time_ps: storyForm.read_time }
      : { title_da: storyForm.title, excerpt_da: storyForm.excerpt, content_da: storyForm.content, author_da: storyForm.author, author_role_da: storyForm.author_role, read_time_da: storyForm.read_time };
    await supabase.from('stories').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', selectedStory.id);
    setSaving(false);
    setSaved(true);
    fetchStories();
    setTimeout(() => setSaved(false), 2000);
  };

  const sections = SECTIONS[activePage] ?? [];
  const enRef = ENGLISH_REF[activePage]?.[activeSection] ?? {};

  const renderPageContentTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* English Reference */}
      <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">REFERENCE</span>
          <span className="text-xs text-gray-400">English</span>
        </div>
        {enRef.title && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Title</div>
            <div className="text-sm text-gray-700 leading-relaxed">{enRef.title}</div>
          </div>
        )}
        {enRef.subtitle && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Subtitle</div>
            <div className="text-sm text-gray-700 leading-relaxed">{enRef.subtitle}</div>
          </div>
        )}
        {enRef.body && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Body</div>
            <div className="text-sm text-gray-700 leading-relaxed">{enRef.body}</div>
          </div>
        )}
        {enRef.cta && (
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">CTA</div>
            <div className="text-sm text-gray-700 leading-relaxed">{enRef.cta}</div>
          </div>
        )}
        {!enRef.title && !enRef.subtitle && !enRef.body && !enRef.cta && (
          <div className="text-sm text-gray-400 text-center py-8">No English reference available for this section.</div>
        )}
      </div>

      {/* Editor */}
      <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className={`text-xs font-semibold px-2 py-1 rounded ${isPs ? 'bg-[#4ECDC4]/10 text-[#4ECDC4]' : 'bg-amber-100 text-amber-700'}`}>
            {info.flag} {info.nativeLabel}
          </span>
        </div>
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title</label>
            <input
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
              placeholder={isPs ? 'پښتو کې عنوان ولیکئ...' : 'عنوان را به دری بنویسید...'}
              dir="rtl"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subtitle</label>
            <input
              value={form.subtitle}
              onChange={e => setForm({ ...form, subtitle: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
              placeholder={isPs ? 'پښتو کې فرعي عنوان ولیکئ...' : 'زیرعنوان را به دری بنویسید...'}
              dir="rtl"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Body Content</label>
            <textarea
              value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              rows={4}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none"
              placeholder={isPs ? 'پښتو کې متن ولیکئ...' : 'متن را به دری بنویسید...'}
              dir="rtl"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">CTA Button Text</label>
            <input
              value={form.cta}
              onChange={e => setForm({ ...form, cta: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
              placeholder={isPs ? 'پښتو کې د تڼۍ متن ولیکئ...' : 'متن دکمه را به دری بنویسید...'}
              dir="rtl"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              {language === 'ps' ? 'پښتو' : 'دری'} / {activePage} / {activeSection}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i> Saved!
              </span>
            )}
            <button
              onClick={handleSavePageContent}
              disabled={saving}
              className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
            >
              {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
              Save Content
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProjectsTab = () => (
    <div className="flex gap-5">
      {/* Project List */}
      <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Projects</div>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {projLoading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-[#4ECDC4] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">No projects found. Add projects first.</div>
          ) : (
            projects.map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedProject(p)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${selectedProject?.id === p.id ? 'bg-[#4ECDC4]/5 border-l-2 border-l-[#4ECDC4]' : ''}`}
              >
                <div className="text-sm font-medium text-[#1B2838] line-clamp-2">{p.title_en}</div>
                <div className="text-xs mt-1">
                  {isPs ? (
                    p.title_ps ? <span className="text-[#4ECDC4]" dir="rtl">{p.title_ps}</span> : <span className="text-amber-500 italic">Not translated</span>
                  ) : (
                    p.title_da ? <span className="text-[#4ECDC4]" dir="rtl">{p.title_da}</span> : <span className="text-amber-500 italic">Not translated</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 bg-white rounded-xl border border-gray-100 p-6">
        {!selectedProject ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3"><i className="ri-file-text-line text-3xl"></i></div>
            <p className="text-sm">Select a project from the list to edit its {info.label} translation</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* English Reference */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">REF</span>
                <span className="text-xs text-gray-400">English</span>
              </div>
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Title</div>
                <div className="text-sm text-gray-700 leading-relaxed font-medium">{selectedProject.title_en}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Description</div>
                <div className="text-sm text-gray-700 leading-relaxed">{selectedProject.description_en || 'No English description.'}</div>
              </div>
            </div>

            {/* PS/DA Editor */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${isPs ? 'bg-[#4ECDC4]/10 text-[#4ECDC4]' : 'bg-amber-100 text-amber-700'}`}>
                  {info.flag} {info.nativeLabel}
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title</label>
                  <input
                    value={projectForm.title}
                    onChange={e => setProjectForm({ ...projectForm, title: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                    placeholder={isPs ? 'پښتو کې د پروژې عنوان...' : 'عنوان پروژه به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                  <textarea
                    value={projectForm.description}
                    onChange={e => setProjectForm({ ...projectForm, description: e.target.value })}
                    rows={6}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none"
                    placeholder={isPs ? 'پښتو کې د پروژې تفصیل...' : 'توضیحات پروژه به دری...'}
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-gray-100">
                {saved && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i> Saved!
                  </span>
                )}
                <button
                  onClick={handleSaveProject}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                >
                  {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Save Translation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderVolunteersTab = () => (
    <div className="flex gap-5">
      {/* Opportunity List */}
      <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Opportunities</div>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {oppLoading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-[#4ECDC4] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : opportunities.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">No opportunities found. Add opportunities first.</div>
          ) : (
            opportunities.map(o => (
              <button
                key={o.id}
                onClick={() => setSelectedOpp(o)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${selectedOpp?.id === o.id ? 'bg-[#4ECDC4]/5 border-l-2 border-l-[#4ECDC4]' : ''}`}
              >
                <div className="text-sm font-medium text-[#1B2838] line-clamp-2">{o.title_en}</div>
                <div className="text-xs mt-1">
                  {isPs ? (
                    o.title_ps ? <span className="text-[#4ECDC4]" dir="rtl">{o.title_ps}</span> : <span className="text-amber-500 italic">Not translated</span>
                  ) : (
                    o.title_da ? <span className="text-[#4ECDC4]" dir="rtl">{o.title_da}</span> : <span className="text-amber-500 italic">Not translated</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 bg-white rounded-xl border border-gray-100 p-6">
        {!selectedOpp ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3"><i className="ri-team-line text-3xl"></i></div>
            <p className="text-sm">Select an opportunity from the list to edit its {info.label} translation</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* English Reference */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">REF</span>
                <span className="text-xs text-gray-400">English</span>
              </div>
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Title</div>
                <div className="text-sm text-gray-700 leading-relaxed font-medium">{selectedOpp.title_en}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Description</div>
                <div className="text-sm text-gray-700 leading-relaxed">{selectedOpp.description_en || 'No English description.'}</div>
              </div>
            </div>

            {/* PS/DA Editor */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${isPs ? 'bg-[#4ECDC4]/10 text-[#4ECDC4]' : 'bg-amber-100 text-amber-700'}`}>
                  {info.flag} {info.nativeLabel}
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title</label>
                  <input
                    value={oppForm.title}
                    onChange={e => setOppForm({ ...oppForm, title: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                    placeholder={isPs ? 'پښتو کې د فرصت عنوان...' : 'عنوان فرصت به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                  <textarea
                    value={oppForm.description}
                    onChange={e => setOppForm({ ...oppForm, description: e.target.value })}
                    rows={6}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none"
                    placeholder={isPs ? 'پښتو کې د فرصت تفصیل...' : 'توضیحات فرصت به دری...'}
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-gray-100">
                {saved && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i> Saved!
                  </span>
                )}
                <button
                  onClick={handleSaveOpportunity}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                >
                  {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Save Translation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStoriesTab = () => (
    <div className="flex gap-5">
      {/* Story List */}
      <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stories</div>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {storyLoading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-[#4ECDC4] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : stories.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">No stories found. Create stories first in the Stories dashboard.</div>
          ) : (
            stories.map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedStory(s)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${selectedStory?.id === s.id ? 'bg-[#4ECDC4]/5 border-l-2 border-l-[#4ECDC4]' : ''}`}
              >
                <div className="text-sm font-medium text-[#1B2838] line-clamp-2">{s.title_en}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400">{s.category}</span>
                  {s.published ? (
                    <span className="text-xs text-green-600">Published</span>
                  ) : (
                    <span className="text-xs text-amber-500">Draft</span>
                  )}
                </div>
                <div className="text-xs mt-1">
                  {isPs ? (
                    s.title_ps ? <span className="text-[#4ECDC4]" dir="rtl">{s.title_ps}</span> : <span className="text-amber-500 italic">Not translated</span>
                  ) : (
                    s.title_da ? <span className="text-[#4ECDC4]" dir="rtl">{s.title_da}</span> : <span className="text-amber-500 italic">Not translated</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 bg-white rounded-xl border border-gray-100 p-6">
        {!selectedStory ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3"><i className="ri-book-open-line text-3xl"></i></div>
            <p className="text-sm">Select a story from the list to edit its {info.label} translation</p>
            <p className="text-xs mt-1 text-gray-300">Stories are created in English via the Stories dashboard, then translated here</p>
          </div>
        ) : (
          <>
            {/* English Reference */}
            <div className="mb-6 pb-6 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">REFERENCE</span>
                <span className="text-xs text-gray-400">English — {selectedStory.category}</span>
              </div>
              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Title</div>
                <div className="text-sm text-gray-700 leading-relaxed font-medium">{selectedStory.title_en}</div>
              </div>
              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Excerpt</div>
                <div className="text-sm text-gray-700 leading-relaxed">{selectedStory.excerpt_en || 'No English excerpt.'}</div>
              </div>
              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Author</div>
                <div className="text-sm text-gray-700">{selectedStory.author_en || '—'}{selectedStory.author_role_en ? `, ${selectedStory.author_role_en}` : ''}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Read Time</div>
                <div className="text-sm text-gray-700">{selectedStory.read_time_en || '—'}</div>
              </div>
            </div>

            {/* PS/DA Editor */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${isPs ? 'bg-[#4ECDC4]/10 text-[#4ECDC4]' : 'bg-amber-100 text-amber-700'}`}>
                  {info.flag} {info.nativeLabel} Translation
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title</label>
                  <input
                    value={storyForm.title}
                    onChange={e => setStoryForm({ ...storyForm, title: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                    placeholder={isPs ? 'پښتو کې د کیسې عنوان...' : 'عنوان داستان به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Excerpt</label>
                  <textarea
                    value={storyForm.excerpt}
                    onChange={e => setStoryForm({ ...storyForm, excerpt: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none"
                    placeholder={isPs ? 'پښتو کې د کیسې لنډیز...' : 'خلاصه داستان به دری...'}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Content (HTML)</label>
                  <textarea
                    value={storyForm.content}
                    onChange={e => setStoryForm({ ...storyForm, content: e.target.value })}
                    rows={8}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none font-mono"
                    placeholder={isPs ? 'پښتو کې د کیسې بشپړ متن...' : 'متن کامل داستان به دری...'}
                    dir="rtl"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Author Name</label>
                    <input
                      value={storyForm.author}
                      onChange={e => setStoryForm({ ...storyForm, author: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                      placeholder={isPs ? 'لیکوال...' : 'نویسنده...'}
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Author Role</label>
                    <input
                      value={storyForm.author_role}
                      onChange={e => setStoryForm({ ...storyForm, author_role: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                      placeholder={isPs ? 'د لیکوال رول...' : 'نقش نویسنده...'}
                      dir="rtl"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Read Time</label>
                  <input
                    value={storyForm.read_time}
                    onChange={e => setStoryForm({ ...storyForm, read_time: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                    placeholder={isPs ? 'د لوستلو وخت...' : 'زمان مطالعه...'}
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                {saved && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i> Saved!
                  </span>
                )}
                <button
                  onClick={handleSaveStory}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                >
                  {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Save Translation
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[#1B2838]">{info.label} CMS</h1>
              <span className="text-lg">{info.flag}</span>
            </div>
            <p className="text-gray-500 text-sm mt-0.5">Manually translate and manage all {info.label} content — page sections, projects, volunteer opportunities, and stories</p>
          </div>
        </div>

        {/* Page + Section Selectors (only for pages tab) */}
        {activeTab === 'pages' && (
          <div className="flex flex-wrap items-center gap-4 mb-5">
            <div className="bg-white rounded-xl border border-gray-100 p-1 flex gap-1">
              {PAGES.map(p => (
                <button
                  key={p}
                  onClick={() => { setActivePage(p); setActiveSection(SECTIONS[p]?.[0] ?? 'hero'); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer capitalize transition-colors whitespace-nowrap ${activePage === p ? 'bg-[#1B2838] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-1 flex gap-1">
              {sections.map(s => (
                <button
                  key={s}
                  onClick={() => setActiveSection(s)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer capitalize transition-colors whitespace-nowrap ${activeSection === s ? 'bg-[#4ECDC4] text-[#1B2838]' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {s.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border border-gray-100 p-1.5 flex gap-1 w-fit mb-6">
          {([
            { key: 'pages' as TabType, label: 'Page Content', icon: 'ri-pages-line' },
            { key: 'projects' as TabType, label: 'Projects', icon: 'ri-folder-line' },
            { key: 'volunteers' as TabType, label: 'Volunteer Opportunities', icon: 'ri-team-line' },
            { key: 'stories' as TabType, label: 'Stories', icon: 'ri-book-open-line' },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${activeTab === tab.key ? 'bg-[#1B2838] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'pages' && renderPageContentTab()}
        {activeTab === 'projects' && renderProjectsTab()}
        {activeTab === 'volunteers' && renderVolunteersTab()}
        {activeTab === 'stories' && renderStoriesTab()}
      </main>
    </div>
  );
}